import { useState } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="text-xs text-zinc-600">unknown</span>;
  const colors: Record<string, string> = {
    active: "bg-green-500/15 text-green-400 border-green-500/30",
    shutoff: "bg-zinc-700/40 text-zinc-400 border-zinc-600",
    stopped: "bg-zinc-700/40 text-zinc-400 border-zinc-600",
    starting: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    stopping: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    building: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    error: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  const cls = colors[status.toLowerCase()] || "bg-zinc-800 text-zinc-400 border-zinc-700";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${cls}`}>
      {status}
    </span>
  );
}

function ServerCard({
  server,
  onRemove,
}: {
  server: any;
  onRemove: (id: Id<"devServers">) => void;
}) {
  const control = useAction(api.devServers.controlServer);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAction = async (action: "start" | "stop" | "status") => {
    setLoading(action);
    setError(null);
    try {
      await control({ id: server._id, action });
    } catch (e: any) {
      setError(e.message || "Action failed");
    } finally {
      setLoading(null);
    }
  };

  const isActive = server.lastStatus?.toLowerCase() === "active";

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100">{server.name}</h3>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            {server.instanceId}
          </p>
        </div>
        <StatusBadge status={server.lastStatus} />
      </div>

      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <span className="text-zinc-500">Project</span>
          <p className="text-zinc-300 font-mono mt-0.5">{server.projectId}</p>
        </div>
        <div>
          <span className="text-zinc-500">Region</span>
          <p className="text-zinc-300 font-mono mt-0.5">{server.regionId}</p>
        </div>
        <div>
          <span className="text-zinc-500">Last checked</span>
          <p className="text-zinc-300 mt-0.5">
            {server.lastCheckedAt
              ? new Date(server.lastCheckedAt).toLocaleTimeString()
              : "never"}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded px-3 py-2 text-xs text-red-400">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => handleAction("status")}
          disabled={loading !== null}
          className="px-3 py-1.5 text-xs rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600 transition-colors disabled:opacity-40"
        >
          {loading === "status" ? "Checking..." : "Refresh status"}
        </button>
        {isActive ? (
          <button
            onClick={() => handleAction("stop")}
            disabled={loading !== null}
            className="px-3 py-1.5 text-xs rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
          >
            {loading === "stop" ? "Stopping..." : "Stop"}
          </button>
        ) : (
          <button
            onClick={() => handleAction("start")}
            disabled={loading !== null}
            className="px-3 py-1.5 text-xs rounded border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-40"
          >
            {loading === "start" ? "Starting..." : "Start"}
          </button>
        )}
        <button
          onClick={() => onRemove(server._id)}
          className="ml-auto px-3 py-1.5 text-xs rounded text-zinc-600 hover:text-red-400 transition-colors"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function AddServerForm({ onDone }: { onDone: () => void }) {
  const createServer = useMutation(api.devServers.create);
  const [form, setForm] = useState({
    name: "",
    apiKey: "",
    projectId: "",
    regionId: "",
    instanceId: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.apiKey || !form.projectId || !form.regionId || !form.instanceId) return;
    setSaving(true);
    setError(null);
    try {
      await createServer(form);
      onDone();
    } catch (e: any) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-indigo-500 focus:outline-none";

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
      <h3 className="text-sm font-semibold text-zinc-100">Add Gcore Server</h3>

      <div className="space-y-3">
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Name</label>
          <input
            className={inputCls}
            placeholder="My dev server"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">API Token</label>
          <input
            className={inputCls}
            type="password"
            placeholder="Gcore API token"
            value={form.apiKey}
            onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Project ID</label>
            <input
              className={inputCls}
              placeholder="484964"
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Region ID</label>
            <input
              className={inputCls}
              placeholder="14"
              value={form.regionId}
              onChange={(e) => setForm({ ...form, regionId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Instance ID</label>
            <input
              className={inputCls}
              placeholder="0b16a5cb-..."
              value={form.instanceId}
              onChange={(e) => setForm({ ...form, instanceId: e.target.value })}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded px-3 py-2 text-xs text-red-400">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-40"
        >
          {saving ? "Saving..." : "Add server"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="px-4 py-2 text-sm rounded border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function DevServerPage() {
  const servers = useQuery(api.devServers.listSafe);
  const removeServer = useMutation(api.devServers.remove);
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Dev Servers</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Manage cloud VMs — start and stop from here.
          </p>
        </div>
        {!showAdd && (
          <button
            onClick={() => setShowAdd(true)}
            className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
          >
            + Add server
          </button>
        )}
      </div>

      {showAdd && <AddServerForm onDone={() => setShowAdd(false)} />}

      {servers === undefined ? (
        <div className="text-sm text-zinc-600">Loading...</div>
      ) : servers.length === 0 && !showAdd ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-sm">No servers configured yet.</p>
          <button
            onClick={() => setShowAdd(true)}
            className="text-sm text-indigo-400 hover:text-indigo-300 mt-2"
          >
            Add your first server
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {servers.map((s) => (
            <ServerCard
              key={s._id}
              server={s}
              onRemove={(id) => removeServer({ id })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
